/**
 * Tansy · ações do carrinho e dos cards de kit
 *
 * - form[data-velune-add]: adiciona o produto ao carrinho sem recarregar a página (sugestões do carrinho,
 *   como o bloco "Complete your trio", e cards da seção "Complete a rotina"). Usa o mesmo evento de carrinho
 *   do Horizon, então o carrinho lateral abre e se atualiza sozinho. Sem JavaScript, o formulário funciona do
 *   jeito normal.
 *   Dentro de um [data-velune-add-group], só um formulário envia por vez: um segundo "Add" tocado enquanto o
 *   primeiro não terminou é ignorado (o grupo fica com aria-busy). Nesses grupos o evento de carrinho só sai
 *   depois de a loja aceitar o item, para a mensagem de erro não sumir com o redesenho do carrinho. Enquanto o
 *   "Add" vai, o carrinho inteiro fica desligado (classe cart-items-disabled, a mesma que o tema usa ao mudar
 *   uma quantidade): a lixeira e a quantidade mandam o NÚMERO da linha, e o item novo entra no alto da lista.
 * - select[data-velune-add-select]: ao trocar a opção, mostra o preço dela (data-price-text da linha
 *   escolhida) no [data-velune-add-price] do mesmo [data-velune-add-item]. A opção escolhida fica guardada
 *   pelo id da caixa e é reposta depois de cada redesenho do carrinho (o HTML da loja volta sempre com a
 *   primeira opção disponível).
 * - select[data-velune-line-variant]: a caixa de escolha da opção em cada linha do carrinho
 *   (snippets/cart-products.liquid). Ao trocar, a linha passa para a variante escolhida, com a mesma quantidade
 *   e as mesmas propriedades, sem sair da página (ver "Troca de variante na linha", mais abaixo).
 * - Gaveta do carrinho no celular: arrastar o dedo para a direita fecha a gaveta (como na referência).
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';

const FLAG = '__veluneCartActionsReady';

/** @returns {string[]} */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter((id) => typeof id === 'string' && id.length > 0);
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showError(form, message) {
  const element = /** @type {HTMLElement | null} */ (form.querySelector('[data-role="error"]'));
  if (!element) return;
  element.textContent = message;
  element.hidden = false;
}

/** @param {HTMLFormElement} form */
function hideError(form) {
  const element = /** @type {HTMLElement | null} */ (form.querySelector('[data-role="error"]'));
  if (!element) return;
  element.textContent = '';
  element.hidden = true;
}

/**
 * As opções que a cliente escolheu nas sugestões: id da caixa de escolha → id da variante.
 * Só entram escolhas feitas por ela (evento change). Valem enquanto a página estiver aberta.
 * @type {Map<string, string>}
 */
const chosenOptions = new Map();

/**
 * Mostra, na sugestão, o preço da opção escolhida na caixa.
 * @param {HTMLSelectElement} select
 */
function syncAddPrice(select) {
  const item = select.closest('[data-velune-add-item]');
  const price = item?.querySelector('[data-velune-add-price]');
  const text = select.selectedOptions[0]?.dataset.priceText;
  if (price && text && price.textContent !== text) price.textContent = text;
}

/**
 * Repõe as opções escolhidas depois de o carrinho ser redesenhado. O HTML que vem da loja traz sempre a primeira
 * opção disponível, e o redesenho (assets/morph.js) copia isso para as caixas que ficaram na página: sem este
 * passo, quem escolhe a cor da segunda sugestão e depois adiciona a primeira levaria a cor errada.
 * Uma opção que deixou de existir ou esgotou não é reposta (fica a que a loja mandou).
 */
function restoreChosenOptions() {
  if (chosenOptions.size === 0) return;
  for (const select of document.querySelectorAll('select[data-velune-add-select]')) {
    if (!(select instanceof HTMLSelectElement) || !select.id) continue;
    const wanted = chosenOptions.get(select.id);
    if (wanted === undefined) continue;
    if (select.value !== wanted) {
      const option = Array.from(select.options).find((candidate) => candidate.value === wanted);
      if (option && !option.disabled) {
        select.value = wanted;
      } else {
        chosenOptions.delete(select.id);
      }
    }
    syncAddPrice(select);
  }
}

/**
 * Todo redesenho do carrinho (um "Add", uma quantidade, uma linha tirada) passa por este evento do tema e
 * acontece dentro da promessa dele; o setTimeout roda logo depois.
 * @param {Event} event
 */
function onCartLinesUpdate(event) {
  if (chosenOptions.size === 0) return;
  const promise = /** @type {any} */ (event).promise;
  if (!promise || typeof promise.then !== 'function') return;
  const restore = () => setTimeout(restoreChosenOptions, 0);
  promise.then(restore, restore);
}

/** @returns {Promise<any>} O carrinho como está na loja */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

/**
 * Depois de um "Add" feito num grupo de sugestões (o carrinho já foi redesenhado):
 * - na gaveta, o item novo entra no alto da lista. A lista fica no topo, mostrando o item que acabou de entrar
 *   (pedido do Eric em 05/10/2026; antes ela rolava até a sugestão que sobrou, e o item novo saía da vista).
 *   A linha nova entra crescendo (animação do tema, "adding"), então o topo é reposto quando ela termina;
 * - na página do carrinho, quando a última sugestão entra o bloco some. No celular ele fica depois do cartão
 *   do total, então a tela pararia mais abaixo, sem o desconto que acabou de entrar: o cartão do total (com a
 *   frase "You're saving…" e o Checkout) é trazido para a vista, se não estiver;
 * - o botão tocado some junto com a sugestão. Se o foco estava nele e se perdeu, vai para o "Add" seguinte ou,
 *   sem mais sugestões, para o botão principal do carrinho (View cart ou Checkout), sem rolar a lista.
 * O carrinho é redesenhado dentro da promessa do evento; o setTimeout roda depois disso.
 * @param {Element | null} root - O cart-items-component de onde saiu o envio
 * @param {boolean} hadFocus - Se o foco estava dentro do grupo na hora do envio
 */
function afterGroupAdd(root, hadFocus) {
  if (!(root instanceof HTMLElement)) return;
  setTimeout(() => {
    const group = root.querySelector('[data-velune-add-group]');

    if (root.dataset.drawer !== undefined) {
      // Quem rola é a lista; em tela muito baixa (rodapé solto), o conteúdo inteiro
      const toTop = () => {
        for (const scroller of root.querySelectorAll('.cart-drawer__items, .cart-drawer__content')) {
          if (scroller.scrollTop !== 0) scroller.scrollTop = 0;
        }
      };
      /** @param {Event} event */
      const onRowGrown = (event) => {
        if (event.target instanceof Element && event.target.matches('.cart-items__table-row')) toTop();
      };
      toTop();
      root.addEventListener('animationend', onRowGrown);
      setTimeout(() => root.removeEventListener('animationend', onRowGrown), 1500);
    } else if (!group) {
      const totals = root.querySelector('.mm-sum__card--totals');
      if (totals instanceof HTMLElement) {
        const box = totals.getBoundingClientRect();
        if (box.top < 0 || box.bottom > window.innerHeight) totals.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    }

    const active = document.activeElement;
    if (!hadFocus || (active && active !== document.body && active.isConnected)) return;
    const next = group?.querySelector('button[type="submit"]') ?? root.querySelector('.mm-sum__cta');
    if (next instanceof HTMLElement) next.focus({ preventScroll: true });
  }, 0);
}

/** @param {SubmitEvent} event */
async function onAddSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form[data-velune-add]')) return;

  event.preventDefault();

  const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('[type="submit"]'));
  if (button?.disabled) return;
  const group = form.closest('[data-velune-add-group]');
  // Guardado agora: depois de o carrinho ser redesenhado, o formulário pode já não estar na página
  const cartRoot = form.closest('cart-items-component');
  if (group?.getAttribute('aria-busy') === 'true') return;
  // Uma quantidade ou uma lixeira ainda indo: espera (ver o alto do arquivo)
  if (group && cartRoot?.classList.contains('cart-items-disabled')) return;
  // Uma troca de variante ainda indo: espera também
  if (group && cartRoot && swappingRoots.has(cartRoot)) return;
  group?.setAttribute('aria-busy', 'true');
  if (group) cartRoot?.classList.add('cart-items-disabled');
  const hadFocus = Boolean(group && group.contains(document.activeElement));
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  hideError(form);

  const formData = new FormData(form);
  const quantity = Number(formData.get('quantity') || 1) || 1;
  // Um produto (campo "id") ou vários de uma vez (campos "items[n][id]", a caixinha do trio no carrinho)
  /** @type {{ id: number, quantity: number }[]} */
  const items = [];
  for (const [key, value] of formData.entries()) {
    if (key !== 'id' && !/^items\[\d+\]\[id\]$/.test(key)) continue;
    const id = Number(value);
    if (id) items.push({ id, quantity });
  }
  if (!items.length) return;
  const context = form.dataset.veluneAdd === 'cart' ? 'cart' : 'product';

  const deferred = CartLinesUpdateEvent.createPromise();
  const announce = () =>
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  // Num grupo de sugestões (dentro do carrinho) o evento só sai depois de a loja aceitar o item. Avisado
  // antes, um erro faria o carrinho se redesenhar e a mensagem de erro sumiria junto com o formulário.
  const announceLate = Boolean(group);
  if (!announceLate) announce();

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
    });
    const result = await response.json();

    // A resposta do "Add" decide sozinha se deu certo; a leitura do carrinho vem depois e à parte.
    if (!response.ok || result.status) {
      const message = result.description || result.message || 'Something went wrong. Please try again.';
      showError(form, message);
      form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      if (!announceLate) {
        // O evento já saiu: a promessa dele fecha com o carrinho como está
        try {
          const cart = await fetchCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { didError: true, items: cart.items, source: 'velune-cart' },
          });
        } catch (error) {
          deferred.reject(error);
        }
      }
      return;
    }

    // A loja aceitou o item. Se a leitura do carrinho falhar (rede), tenta mais uma vez; sem resposta, abre a
    // página do carrinho, como o formulário faria sem JavaScript. Mostrar erro aqui levaria a cliente a tocar
    // em "Add" de novo e pôr uma segunda unidade.
    let cart = null;
    for (let attempt = 0; attempt < 2 && !cart; attempt++) {
      cart = await fetchCart().catch(() => null);
    }
    if (!cart) {
      if (!announceLate) deferred.reject(new Error('Cart request failed'));
      window.location.assign(Theme.routes.cart_url);
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: result.sections,
        source: 'velune-cart',
        itemCount: quantity * items.length,
        didError: false,
      },
    });
    if (announceLate) announce();
    if (group) afterGroupAdd(cartRoot, hadFocus);
  } catch (error) {
    if (!announceLate) deferred.reject(error);
    showError(form, 'Something went wrong. Please try again.');
    form.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
  } finally {
    group?.removeAttribute('aria-busy');
    if (group) cartRoot?.classList.remove('cart-items-disabled');
    if (button && button.isConnected) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

/** @param {Event} event */
function onAddSelectChange(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('select[data-velune-add-select]')) return;

  if (select.id) chosenOptions.set(select.id, select.value);
  syncAddPrice(select);

  const form = select.closest('form');
  if (form instanceof HTMLFormElement) hideError(form);
}

// ---------------------------------------------------------------------------------------------------------
// Troca de variante na linha do carrinho (select[data-velune-line-variant])
// ---------------------------------------------------------------------------------------------------------
//
// O pedido à loja (revisão de 08/10/2026): a variante nova entra SEMPRE pelo /cart/add.js, que é a rota em que a
// loja confere o estoque somando o que já está no carrinho, e a linha antiga só sai depois de a nova ter
// entrado inteira. Antes, a linha comum ia num pedido só pelo /cart/update.js (com a soma das quantidades
// quando a variante nova já estava noutra linha), e essa rota aceita quantidade acima do estoque.
// São quatro pedidos:
// 1. a leitura do carrinho, para saber quantas unidades da variante nova já estão nele;
// 2. /cart/add.js com a variante nova, a quantidade e as propriedades da linha;
// 3. outra leitura do carrinho: confere quantas unidades entraram de verdade. Se a loja recusou, ou pôs menos
//    do que o pedido (estoque), o que entrou é devolvido, a linha antiga fica como estava, o carrinho é
//    redesenhado como está na loja e a mensagem aparece na linha;
// 4. /cart/change.js zerando a linha antiga, pelo número dela nessa leitura (a chave das linhas muda quando o
//    desconto da loja entra ou sai, então a chave antiga pode já não valer).
// A linha trocada vira uma linha nova, e a Shopify põe linha nova no alto da lista: a linha pode mudar de
// lugar. Depois do redesenho o foco vai para a caixa de escolha dela, onde ela estiver, e ela é trazida para a
// vista. Quando a variante nova já estava no carrinho, as duas linhas viram uma, com as quantidades somadas.
// Enquanto a troca vai, o carrinho não recebe toque nem teclado (ver "swappingRoots"). A quantidade do tema
// espera 300 ms antes de ir à loja e manda o NÚMERO da linha: uma quantidade tocada logo antes da troca é
// esperada primeiro, e a troca usa a quantidade que ficou.

const GENERIC_ERROR = 'Something went wrong. Please try again.';
const STOCK_ERROR = 'Not enough stock for that option. Your cart was not changed.';
const HALF_ERROR = 'We added the new option but could not remove this one. Please remove it if you do not want it.';

/** Carrinhos (cart-items-component) com uma troca de variante em curso. @type {WeakSet<Element>} */
const swappingRoots = new WeakSet();

/** @param {number} ms */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<any>} A resposta da loja. Recusa (estoque, limite) vira erro com a mensagem dela.
 */
async function postCart(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok || result.status || result.errors) {
    const text = result.description || result.message || result.errors;
    const error = new Error(typeof text === 'string' && text ? text : GENERIC_ERROR);
    error.name = 'CartRefused';
    throw error;
  }
  return result;
}

/**
 * A caixa de escolha de uma linha, procurada de novo no carrinho como ele está agora (o redesenho pode trocar
 * os nós da linha).
 * @param {Element} root
 * @param {string} variantId
 * @param {string} properties - O JSON das propriedades da linha ('' sem propriedades)
 * @returns {HTMLSelectElement | null}
 */
function findLineSelect(root, variantId, properties) {
  for (const select of root.querySelectorAll('select[data-velune-line-variant]')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    if (select.dataset.variantId === variantId && (select.dataset.properties || '') === properties) return select;
  }
  return null;
}

/**
 * @param {HTMLSelectElement} select
 * @param {string} message
 */
function showLineError(select, message) {
  const row = select.closest('.cart-items__table-row');
  const box = row?.querySelector('.cart-items__error');
  const text = box?.querySelector('.cart-item__error-text');
  if (!box || !text) return;
  text.textContent = message;
  box.classList.remove('hidden');
}

/**
 * Quantas unidades de uma variante estão no carrinho, somando todas as linhas dela.
 * @param {any} cart
 * @param {string} variantId
 * @returns {number}
 */
function variantUnits(cart, variantId) {
  let total = 0;
  for (const line of cart?.items ?? []) {
    if (String(line.variant_id) === variantId) total += Number(line.quantity) || 0;
  }
  return total;
}

/**
 * Devolve o que entrou a mais de uma variante (a loja pôs só parte do pedido): cada linha dela volta à
 * quantidade que tinha na leitura de antes. Só mexe quando dá para saber qual linha cresceu.
 * @param {any} before - O carrinho antes do pedido
 * @param {any} now - O carrinho depois do pedido
 * @param {string} variantId
 */
async function undoPartialAdd(before, now, variantId) {
  /** @param {any} line */
  const isVariant = (line) => String(line.variant_id) === variantId;
  const previous = before.items.filter(isVariant);
  const current = now.items.filter(isVariant);
  for (const line of current) {
    const same = previous.find((/** @type {any} */ candidate) => candidate.key === line.key);
    let was = same ? Number(same.quantity) || 0 : null;
    // Linha que não existia antes: só é certo que é nova quando a variante não estava no carrinho
    if (was === null && previous.length === 0) was = 0;
    // Uma linha só antes e uma só agora: é a mesma, mesmo que a chave tenha mudado
    if (was === null && previous.length === 1 && current.length === 1) was = Number(previous[0].quantity) || 0;
    if (was === null || Number(line.quantity) <= was) continue;
    await postCart(Theme.routes.cart_change_url, { line: now.items.indexOf(line) + 1, quantity: was });
    // Uma mudança por vez: o número das linhas pode mudar quando uma delas sai
    return;
  }
}

/**
 * Redesenha o carrinho como ele está na loja (depois de uma recusa ou de uma troca que ficou pela metade).
 * Busca o carrinho e o HTML das seções dele; com o HTML em mãos, o carrinho do tema troca a tela na hora.
 * @param {Element} root
 * @param {string} variantId
 */
async function redrawFromStore(root, variantId) {
  const ids = cartSectionIds().join(',');
  const [cart, sections] = await Promise.all([
    fetchCart(),
    ids
      ? fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : undefined))
          .catch(() => undefined)
      : Promise.resolve(undefined),
  ]);
  const deferred = CartLinesUpdateEvent.createPromise();
  (root.firstElementChild ?? root).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ id: variantId, quantity: variantUnits(cart, variantId) }],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      sections: sections ?? undefined,
      source: 'velune-cart',
      itemCount: Number(cart.item_count) || 0,
      didError: true,
    },
  });
  // O redesenho acontece na continuação da promessa: a vez seguinte já encontra a tela nova
  await sleep(0);
}

/**
 * Espera a quantidade que ainda está indo: o tema segura cada mudança 300 ms antes de mandar (o campo já
 * mostra o número novo e o atributo value ainda tem o antigo) e deixa o carrinho com cart-items-disabled
 * enquanto a loja responde.
 * @param {Element} root
 */
async function waitForQuantity(root) {
  const pending = () =>
    root.classList.contains('cart-items-disabled') ||
    Array.from(root.querySelectorAll('.cart-items__table input[data-cart-line]')).some(
      (input) => input instanceof HTMLInputElement && input.value !== input.defaultValue
    );
  for (let turn = 0; turn < 80 && pending(); turn++) await sleep(100);
}

/**
 * Depois da troca (o carrinho já foi redesenhado): o foco vai para a caixa de escolha da linha trocada, que
 * pode ter mudado de lugar, e ela é trazida para a vista se não estiver inteira nela.
 * @param {Element} root
 * @param {string} variantId
 * @param {string} properties
 * @param {boolean} hadFocus
 */
function afterLineSwap(root, variantId, properties, hadFocus) {
  setTimeout(() => {
    const select = findLineSelect(root, variantId, properties);
    const row = select?.closest('.cart-items__table-row');
    if (!select || !(row instanceof HTMLElement)) return;

    const list = row.closest('.cart-drawer__items');
    const frame = list instanceof HTMLElement ? list.getBoundingClientRect() : null;
    const top = Math.max(0, frame ? frame.top : 0);
    const bottom = Math.min(window.innerHeight, frame ? frame.bottom : window.innerHeight);
    const box = row.getBoundingClientRect();
    if (box.top < top || box.bottom > bottom) row.scrollIntoView({ block: 'center', inline: 'nearest' });

    const active = document.activeElement;
    if (hadFocus || !active || active === document.body || !active.isConnected) select.focus({ preventScroll: true });
  }, 0);
}

/** @param {Event} event */
async function onLineVariantChange(event) {
  const picked = event.target;
  if (!(picked instanceof HTMLSelectElement) || !picked.matches('select[data-velune-line-variant]')) return;

  const root = picked.closest('cart-items-component');
  const oldId = picked.dataset.variantId || '';
  const newId = picked.value;
  const properties = picked.dataset.properties || '';
  if (!(root instanceof HTMLElement) || !oldId || newId === oldId || !/^\d+$/.test(newId)) return;

  // Outra troca ou um "Add" de sugestão ainda indo: esta não vale, e a caixa volta ao que era
  if (swappingRoots.has(root) || root.querySelector('[data-velune-add-group][aria-busy="true"]')) {
    picked.value = oldId;
    return;
  }

  swappingRoots.add(root);
  root.setAttribute('data-velune-swapping', '');
  picked.closest('.cart-items__table-row')?.setAttribute('aria-busy', 'true');
  const hadFocus = document.activeElement === picked;
  /** @type {HTMLSelectElement | null} */
  let select = picked;
  // A loja já recebeu algum pedido desta troca: num erro, a tela é redesenhada com o carrinho de verdade
  let touched = false;
  // A variante nova entrou inteira e a antiga ainda não saiu
  let halfDone = false;

  try {
    await waitForQuantity(root);

    // A linha como está agora (a quantidade esperada pode ter redesenhado o carrinho)
    select = findLineSelect(root, oldId, properties);
    if (!select) {
      // A linha saiu do carrinho enquanto se esperava: não há o que trocar
      if (picked.isConnected) picked.value = picked.dataset.variantId || oldId;
      return;
    }
    if (select.value !== newId) select.value = newId;
    const row = select.closest('.cart-items__table-row');
    const oldKey = select.dataset.lineKey || '';
    const quantity = Number(select.dataset.quantity) || 1;
    if (!oldKey) throw new Error('Line key missing');

    root.classList.add('cart-items-disabled');
    row?.setAttribute('aria-busy', 'true');
    row?.querySelector('.cart-items__error')?.classList.add('hidden');

    const sections = cartSectionIds().join(',');
    const sectionsUrl = window.location.pathname;
    /** @type {Record<string, any> | null} */
    let lineProperties = null;
    if (properties) lineProperties = JSON.parse(properties);

    // 1. Quantas unidades da variante nova a loja já tem neste carrinho
    const before = await fetchCart();
    const unitsBefore = variantUnits(before, newId);

    // 2. A variante nova entra pela rota que confere o estoque
    /** @type {Record<string, any>} */
    const item = { id: Number(newId), quantity };
    if (lineProperties) item.properties = lineProperties;
    touched = true;
    /** @type {unknown} */
    let addError = null;
    try {
      await postCart(Theme.routes.cart_add_url, { items: [item] });
    } catch (error) {
      addError = error;
    }

    // 3. O que entrou de verdade
    const now = await fetchCart();
    const added = variantUnits(now, newId) - unitsBefore;
    if (addError || added < quantity) {
      // A loja recusou ou pôs só parte: o que entrou volta, e a linha antiga fica como estava
      if (added > 0) await undoPartialAdd(before, now, newId).catch(() => {});
      if (addError) throw addError;
      const error = new Error(STOCK_ERROR);
      error.name = 'CartRefused';
      throw error;
    }
    halfDone = true;

    // 4. A linha antiga sai, pelo número dela como o carrinho está agora
    const wanted = JSON.stringify(lineProperties || {});
    /** @param {any} line */
    const isOldLine = (line) => String(line.variant_id) === oldId && line.quantity === quantity;
    let index = now.items.findIndex((/** @type {any} */ line) => line.key === oldKey && String(line.variant_id) === oldId);
    if (index === -1) {
      index = now.items.findIndex(
        (/** @type {any} */ line) => isOldLine(line) && JSON.stringify(line.properties || {}) === wanted
      );
    }
    // As propriedades escritas de outro jeito pela loja: vale a linha da variante antiga com a mesma quantidade
    if (index === -1) index = now.items.findIndex(isOldLine);
    /** @type {any} */
    let cart;
    if (index === -1) {
      cart = now;
    } else {
      cart = await postCart(Theme.routes.cart_change_url, {
        line: index + 1,
        quantity: 0,
        sections,
        sections_url: sectionsUrl,
      });
    }
    halfDone = false;

    // Redesenho pelo evento de carrinho do tema (sem "sections" na resposta, o carrinho busca a seção sozinho)
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: cart.sections ?? undefined,
        source: 'velune-cart',
        itemCount: Number(cart.item_count) || 0,
        didError: false,
      },
    });
    (select.isConnected ? select : root.firstElementChild ?? root).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: oldKey, quantity: 0 },
          { id: newId, quantity },
        ],
        promise: deferred.promise,
      })
    );
    afterLineSwap(root, newId, properties, hadFocus);
  } catch (error) {
    const refused = error instanceof Error && error.name === 'CartRefused';
    // Pela metade: a variante nova entrou e a antiga ficou (a cliente tira a que não quer)
    const message = halfDone ? HALF_ERROR : refused ? error.message : GENERIC_ERROR;
    root.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Network error',
        code: refused ? 'INVALID' : 'SERVICE_UNAVAILABLE',
      })
    );
    // A loja pode ter mexido no carrinho (item que entrou, quantidade ajustada): a tela passa a mostrar o
    // carrinho de verdade, e só depois a mensagem aparece na linha (o redesenho a apagaria)
    if (touched) await redrawFromStore(root, newId).catch(() => {});
    const current = findLineSelect(root, oldId, properties);
    if (current) {
      current.value = oldId;
      showLineError(current, message);
    }
  } finally {
    swappingRoots.delete(root);
    root.removeAttribute('data-velune-swapping');
    root.classList.remove('cart-items-disabled');
    for (const busy of root.querySelectorAll('.cart-items__table-row[aria-busy]')) busy.removeAttribute('aria-busy');
  }
}

/**
 * Com uma troca de variante em curso, o carrinho não aceita clique (lixeira, mais e menos, pelo teclado; o
 * toque já está desligado no CSS) nem mudança de quantidade: os dois mandam o número da linha, que a troca
 * muda. O campo de quantidade volta ao número que tinha.
 * @param {Event} event
 */
function onCartActionWhileSwapping(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const root = target.closest('cart-items-component');
  if (!root || !swappingRoots.has(root) || !target.closest('.cart-items__table')) return;

  event.stopImmediatePropagation();
  if (event.type === 'click') {
    event.preventDefault();
    return;
  }
  const input = target.closest('.quantity-selector')?.querySelector('input');
  if (input instanceof HTMLInputElement) input.value = input.defaultValue;
}

if (!(/** @type {any} */ (window)[FLAG])) {
  /** @type {any} */ (window)[FLAG] = true;
  document.addEventListener('submit', onAddSubmit);
  document.addEventListener('change', onAddSelectChange);
  document.addEventListener('change', onLineVariantChange);
  // No window e na fase de captura: antes dos ouvintes do tema. Os cliques declarados (on:click, como a
  // lixeira) são tratados pelo tema no document, também na captura, e o ouvinte dele foi registrado antes.
  window.addEventListener('click', onCartActionWhileSwapping, true);
  window.addEventListener(ThemeEvents.quantitySelectorUpdate, onCartActionWhileSwapping, true);
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
}

// Carrossel simples com setas (sugestões do carrinho e fotos de clientes). Definido aqui porque o carrinho
// é redesenhado por HTML novo, e scripts que chegam dentro dele não rodam.
if (!customElements.get('velune-scroller')) {
  customElements.define(
    'velune-scroller',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const btn = event.target instanceof Element ? event.target.closest('[data-dir]') : null;
          if (!btn || !this.contains(btn)) return;
          const track = this.querySelector('ul');
          if (!track) return;
          const item = track.firstElementChild;
          const step = item ? item.getBoundingClientRect().width + 10 : track.clientWidth;
          track.scrollBy({ left: step * Number(btn.getAttribute('data-dir')), behavior: 'smooth' });
        });
      }
    }
  );
}

// Proteção do pedido: o interruptor adiciona o produto (mesmo caminho da sugestão) ou zera a linha dele.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const toggle = /** @type {HTMLButtonElement | null} */ (target.closest('[data-velune-protection]'));
  if (!toggle || toggle.getAttribute('aria-busy') === 'true') return;
  toggle.setAttribute('aria-busy', 'true');
  const line = Number(toggle.dataset.line);
  if (toggle.getAttribute('aria-checked') === 'true' && line > 0) {
    toggle.setAttribute('aria-checked', 'false');
    toggle.dispatchEvent(new QuantitySelectorUpdateEvent(0, line));
    return;
  }
  toggle.setAttribute('aria-checked', 'true');
  const form = document.createElement('form');
  form.hidden = true;
  form.method = 'post';
  form.action = Theme.routes.cart_add_url;
  form.dataset.veluneAdd = 'cart';
  form.innerHTML = `<input type="hidden" name="id" value="${Number(toggle.dataset.variantId)}"><input type="hidden" name="quantity" value="1">`;
  toggle.after(form);
  form.requestSubmit();
});

// Gaveta do carrinho: arrastar para fechar mudou para assets/velune-mm.js ("Arrastar para fechar"), onde a gaveta
// segue o dedo 1:1 em vez de só fechar no fim do gesto (revisão de design, 16/10/2026).

